import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  TagDefinition,
  tagDefinitionSelector,
  tagDefinitionSourceKind,
  useGetTagDefinitionQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  TagDefinitionForm,
  TagDefinitionFormDefaultValues,
  TagDefinitionFormValues
} from "../../components/form/TagDefinitionForm/TagDefinitionForm";
import {useTagDefinitionMutation} from "../../hooks/useTagDefinitionMutation";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {toFormDefaultsFromTagDefinition} from "../../components/form/TagDefinitionForm/useTagDefinitionForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: TagDefinitionFormDefaultValues
}

export const TagDefinitionFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
      <TagDefinitionFormPage
          id={id}
          copy={copy}
          formDefaults={formDefaults}
      />
  );
};

export const TagDefinitionFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectTagDefinition = useMemo(() => {
    return tagDefinitionSelector()
  }, [])

  const {
    currTagDefinition,
    isLoading
  } = useGetTagDefinitionQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currTagDefinition: selectTagDefinition(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: TagDefinitionFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromTagDefinition(currTagDefinition);
  }, [currTagDefinition, formDefaults]);

  const isEdit = useMemo(() => {
    return currTagDefinition && !copy
  }, [currTagDefinition, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("common.page.entity.tagdefinition.form.title.edit", "Edit Tag Definition", {ns: "common"});
    } else {
      return t("common.page.entity.tagdefinition.form.title.new", "New Tag Definition", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createTagDefinition,
    updateTagDefinition,
    isCreating,
    isUpdating,
  } = useTagDefinitionMutation()

  const onTagDefinitionSubmit = useCallback((formValues: TagDefinitionFormValues) => {
    const patch: Partial<TagDefinition> = {
      label: formValues.label,
      description: formValues.description,
      colorHex: formValues.colorHex,
      doNotUse: formValues.doNotUse ?? false,
    }

    if (isEdit) {
      const profile: Partial<TagDefinition> = {
        ...currTagDefinition.asTypeValue(),
        ...patch,
      };
      updateTagDefinition(profile, {onSuccess: () => navigate(-1)})
    } else {
      const profile: Partial<TagDefinition> = {
        ...patch,
        sourceKind: tagDefinitionSourceKind.user.code,
      };
      createTagDefinition(profile, {onSuccess: () => navigate(-1)})
    }
  }, [isEdit, currTagDefinition, updateTagDefinition, navigate, createTagDefinition]);

  return (
      <ProtectedPage
          entity={currTagDefinition}
          permissions={[isEdit ? "tagdefinition_edit" : "tagdefinition_create"]}
          isLoading={isLoading}
          onAccess={setAccess}>
        {(classes) => (
            <Container className={classes.container} fluid={true} py="md">
              <Center>
                {isLoading && <Loader/>}
                {!isLoading && (
                    <Paper miw={500} shadow="sm" p="md" py="md">
                      <Group position="apart">
                        <Title align="center">
                          {title}
                        </Title>
                        <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                      </Group>
                      <Space h="md"/>
                      <TagDefinitionForm
                          defaultValues={_formDefaults}
                          submitting={isCreating || isUpdating}
                          onSubmit={onTagDefinitionSubmit}/>
                    </Paper>
                )}
              </Center>
            </Container>
        )}
      </ProtectedPage>
  );
};

